/*
  Workflow Edge Enrichment Engine

  This module handles enriching edges coming from workflow nodes with precedence
  data from the workflow's outcome actions. This ensures that even existing journeys
  without precedence data will have their edges properly ordered.

  Outcomes are read from the workflows metadata that was populated when workflows
  were cached via fetchAndCacheWorkflow.
*/

import type { JourneyDocument, JourneyWorkflowMetadata, WorkflowOutcome } from '../types';

/**
 * Enrich edges from workflow nodes with precedence data
 *
 * This function:
 * 1. Identifies all workflow nodes (workflow, source_workflow, reconciliation_workflow)
 * 2. Gets outcomes from the workflows metadata (populated by fetchAndCacheWorkflow)
 * 3. Finds edges coming from those nodes
 * 4. Attaches the precedence from the workflow's outcome actions to the edges
 *
 * @param doc - The journey document to enrich
 * @param workflows - Workflow metadata with extracted outcomes
 * @returns The enriched document with precedence on workflow edges
 */
export function enrichWorkflowEdgesWithPrecedence(
  doc: JourneyDocument,
  workflows: Record<string, JourneyWorkflowMetadata>,
): JourneyDocument {
  // Identify workflow nodes and collect their tokens
  const workflowNodeToToken: Record<string, string> = {};

  Object.entries(doc.nodes).forEach(([nodeId, node]) => {
    // Check if this is a workflow-type node
    const isWorkflowNode = node.type === 'workflow'
      || node.type === 'source_workflow'
      || node.type === 'reconciliation_workflow';

    if (isWorkflowNode && node.config) {
      const workflowToken = (node.config as any).workflow_token
        || (node.config as any).application_token;

      if (workflowToken) {
        workflowNodeToToken[nodeId] = workflowToken;
      }
    }
  });

  // Enrich edges coming from workflow nodes
  const enrichedEdges = Object.fromEntries(
    Object.entries(doc.edges).map(([edgeId, edge]) => {
      // Skip if edge already has precedence
      if (edge.precedence !== undefined) {
        return [edgeId, edge];
      }

      // Check if this edge comes from a workflow node
      const sourceNodeId = edge.from.node;
      const workflowToken = workflowNodeToToken[sourceNodeId];
      if (!workflowToken) {
        return [edgeId, edge];
      }

      // Get outcomes from workflows metadata
      const workflowMeta = workflows[workflowToken];
      const workflowOutcomes = workflowMeta?.outcomes || [];
      if (workflowOutcomes.length === 0) {
        return [edgeId, edge];
      }

      // Find the outcome matching this edge's condition
      const matchingOutcome = workflowOutcomes.find(
        outcome => outcome.outcome === edge.condition,
      );

      // If we found a matching outcome with precedence, attach it to the edge
      if (matchingOutcome && matchingOutcome.precedence !== undefined) {
        return [edgeId, { ...edge, precedence: matchingOutcome.precedence }];
      }

      return [edgeId, edge];
    }),
  );

  return { ...doc, edges: enrichedEdges };
}

/**
 * Enrich edges from a specific workflow node with precedence data
 *
 * This is a targeted version that only enriches edges from one specific workflow node.
 * Useful when adding or updating a single workflow node.
 *
 * @param doc - The journey document to enrich
 * @param workflowNodeId - The ID of the workflow node
 * @param workflowOutcomes - Array of outcomes with precedence from the workflow version
 * @returns The enriched document with precedence on edges from this workflow node
 */
export function enrichEdgesFromWorkflowNode(
  doc: JourneyDocument,
  workflowNodeId: string,
  workflowOutcomes: WorkflowOutcome[],
): JourneyDocument {
  // Build lookup map for quick access
  const outcomeToPrecedence: Record<string, number> = {};
  workflowOutcomes.forEach(outcome => {
    if (outcome.precedence !== undefined) {
      outcomeToPrecedence[outcome.outcome] = outcome.precedence;
    }
  });

  // Enrich edges from this specific workflow node
  const enrichedEdges = Object.fromEntries(
    Object.entries(doc.edges).map(([edgeId, edge]) => {
      // Only process edges from the specified workflow node
      if (edge.from.node !== workflowNodeId) {
        return [edgeId, edge];
      }

      // Skip if edge already has precedence
      if (edge.precedence !== undefined) {
        return [edgeId, edge];
      }

      // Look up precedence for this edge's condition
      const precedence = edge.condition ? outcomeToPrecedence[edge.condition] : undefined;

      if (precedence !== undefined) {
        return [edgeId, { ...edge, precedence }];
      }

      return [edgeId, edge];
    }),
  );

  return { ...doc, edges: enrichedEdges };
}
